import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '../store';
import {
  getExternalEmrSummary,
  type ExternalEmrCensusSync,
  type ExternalEmrSummary,
} from '../services/externalEmrPatients';
import {
  REFRESH_CENSUS_ERROR,
  censusRefreshResultLine,
} from '../components/patient/externalEmr/externalEmrPatientCopy';

/**
 * The External EMR view's population for the summary. The view hides the
 * population filters, so the counts are the whole census: inactive imported
 * charts and test patients included. The default-view signal reads the same,
 * so its count is the rows it opens.
 */
export const EXTERNAL_EMR_VIEW_SUMMARY_QUERY = {
  show_inactive: true,
  hide_test: false,
} as const;

/** How often the counts are re-read while the census is being read. */
export const CENSUS_POLL_INTERVAL_MS = 10_000;

/** A census refresh the user asked for, waiting for its finish. */
interface PendingCensus {
  /** When Refresh census was pressed (client clock, ms). */
  pressedAt: number;
  /**
   * The `finished_at` on screen at the press. A different finish is a newer
   * run even when the server clock lags the browser's.
   */
  finishedBefore: string | null | undefined;
  /**
   * The run on screen at the press, by its start, and whether it was already
   * failed. `undefined` when the counts never loaded.
   */
  runBefore: { startedAt: string; failed: boolean } | null | undefined;
}

/**
 * True when `sync` is the press's run and it ended after the press.
 *
 * A run left `running` too long reads as failed with no finish time: its
 * worker died. That failure belongs to the press when it is a newer run, or
 * the run that was already running when Refresh was pressed.
 */
function finishedSincePress(
  sync: ExternalEmrCensusSync | null | undefined,
  pending: PendingCensus
): boolean {
  if (!sync || sync.status === 'running') return false;
  if (!sync.finished_at) {
    if (sync.status !== 'failed') return false;
    const before = pending.runBefore;
    return !(before && before.startedAt === sync.started_at && before.failed);
  }
  if (sync.finished_at === pending.finishedBefore) return false;
  return (
    Date.parse(sync.finished_at) > pending.pressedAt ||
    pending.finishedBefore !== undefined
  );
}

/**
 * The External EMR counts for the Patient Manager view, and the census read
 * behind them.
 *
 * The census read runs on the server in the background. A short read can
 * finish before the first poll, so a press does not wait to see `running`:
 * `censusStarted()` records the press, the counts are re-read at once and
 * every 10 s, and the first run that finished after the press is announced.
 * A read already running when the page opens is announced on its step out of
 * `running`. A completed read shows the counts toast and `onCensusSettled`
 * reloads the list; a failed read shows the server's own sentence.
 */
export function useExternalEmrSummary({
  enabled,
  clinicId,
  onCensusSettled,
}: {
  enabled: boolean;
  clinicId: string | null;
  onCensusSettled: () => void;
}) {
  const showError = useStore(state => state.showError);
  const showSuccess = useStore(state => state.showSuccess);
  const [summary, setSummary] = useState<ExternalEmrSummary | null>(null);
  const [failed, setFailed] = useState(false);
  const [pending, setPending] = useState<PendingCensus | null>(null);
  // Counts every settled read, failures included, so the poll re-arms.
  const [reads, setReads] = useState(0);

  const load = useCallback(async () => {
    try {
      setSummary(
        await getExternalEmrSummary({
          clinic_id: clinicId,
          ...EXTERNAL_EMR_VIEW_SUMMARY_QUERY,
        })
      );
      setFailed(false);
    } catch (error) {
      console.error('Failed to load External EMR counts:', error);
      setFailed(true);
    } finally {
      setReads(n => n + 1);
    }
  }, [clinicId]);

  useEffect(() => {
    if (enabled) void load();
  }, [enabled, load]);

  const sync = summary?.census_sync;
  const syncStatus = sync?.status ?? null;

  const announce = useCallback(
    (settled: ExternalEmrCensusSync) => {
      if (settled.status === 'completed' && settled.counts) {
        showSuccess(censusRefreshResultLine(settled.counts));
        onCensusSettled();
      } else if (settled.status === 'failed') {
        // Our own chart-register sentence, written for users.
        showError(settled.error || REFRESH_CENSUS_ERROR);
      }
    },
    [onCensusSettled, showError, showSuccess]
  );

  /** Refresh census was pressed and the server took it. */
  const censusStarted = useCallback(() => {
    setPending({
      pressedAt: Date.now(),
      // `undefined` when the counts never loaded: only the press time decides.
      finishedBefore: summary
        ? (summary.census_sync?.finished_at ?? null)
        : undefined,
      runBefore: summary
        ? summary.census_sync
          ? {
              startedAt: summary.census_sync.started_at,
              failed: summary.census_sync.status === 'failed',
            }
          : null
        : undefined,
    });
    void load();
  }, [load, summary]);

  // Poll while a pressed refresh waits for its finish, and while a read runs.
  // The next poll is set after every read settles, a failed one too.
  useEffect(() => {
    if (!enabled || (!pending && syncStatus !== 'running')) return;
    const timer = setTimeout(() => void load(), CENSUS_POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [enabled, load, pending, reads, syncStatus]);

  // A pressed refresh: announce the first run that finished after the press.
  useEffect(() => {
    if (!pending || !sync || !finishedSincePress(sync, pending)) return;
    setPending(null);
    announce(sync);
  }, [announce, pending, sync]);

  // A read seen running without a press (the page opened mid-run): announce
  // it once, on the step out of running.
  const previous = useRef(syncStatus);
  useEffect(() => {
    const was = previous.current;
    previous.current = syncStatus;
    if (pending || was !== 'running' || syncStatus === 'running') return;
    if (sync) announce(sync);
  }, [announce, pending, sync, syncStatus]);

  return {
    summary,
    failed,
    reload: load,
    censusStarted,
    /** A pressed refresh has not finished yet: keep Refresh census busy. */
    awaitingCensus: pending !== null,
  };
}
